import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ME_KEY } from '../auth/session';
import { api } from './client';
import type { Me } from './types';

/** What a one-time setup link is for (wireframe 15). */
export type SetupPurpose = 'ADMIN_SETUP' | 'SETUP' | 'PASSWORD_RESET';

export interface SetupInfo {
  purpose: SetupPurpose;
  displayName: string;
  email: string;
  needsPrivacyNotice: boolean;
}

/** After the password: the account is ready (and signed in), or the administrator links an authenticator. */
export interface PasswordResult {
  me?: Me;
  totpSecret?: string;
  totpUri?: string;
}

export function useSetupInfo(token: string | undefined) {
  return useQuery({
    queryKey: ['setup', token],
    queryFn: () => api<SetupInfo>(`/api/setup/${token}`),
    enabled: !!token,
    retry: false,
  });
}

export function useSetPassword(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: { password: string; privacyNoticeRead: boolean }) =>
      api<PasswordResult>(`/api/setup/${token}/password`, { method: 'POST', json: request }),
    onSuccess: (result) => {
      if (!result.me) return;
      queryClient.clear();
      queryClient.setQueryData(ME_KEY, result.me);
    },
  });
}

/** The administrator: the first code from the newly linked authenticator app. */
export function useVerifySetup(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api<Me>(`/api/setup/${token}/verify`, { method: 'POST', json: { code } }),
    onSuccess: (me) => {
      queryClient.clear();
      queryClient.setQueryData(ME_KEY, me);
    },
  });
}
